import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  AdminNotification,
  notificationService,
} from "../../services/notificationService";

const ROLE_OPTIONS = [
  "employee",
  "staff",
  "manager",
  "hod",
  "dean",
  "establishment",
  "vc",
  "hr_manager",
  "admin",
  "registrar",
];

const RoleNotificationManagement: React.FC = () => {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetRoles, setTargetRoles] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [editingNotificationId, setEditingNotificationId] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetForm = () => {
    setTitle("");
    setMessage("");
    setTargetRoles([]);
    setIsActive(true);
    setEditingNotificationId(null);
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getAdminNotifications();
      setNotifications(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) {
      setError("Title and message are required");
      return;
    }

    if (targetRoles.length === 0) {
      setError("Select at least one role");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      if (editingNotificationId) {
        await notificationService.updateAdminNotification(
          editingNotificationId,
          {
            title: title.trim(),
            message: message.trim(),
            targetRoles,
            isActive,
          },
        );
        setSuccess("Notification updated successfully");
      } else {
        await notificationService.createAdminNotification({
          title: title.trim(),
          message: message.trim(),
          targetRoles,
        });
        setSuccess("Notification sent to selected roles");
      }

      resetForm();
      await loadNotifications();
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          (editingNotificationId
            ? "Failed to update notification"
            : "Failed to create notification"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditNotification = (notification: AdminNotification) => {
    setEditingNotificationId(notification.id);
    setTitle(notification.title || "");
    setMessage(notification.message || "");
    setTargetRoles(notification.target_roles || []);
    setIsActive(Boolean(notification.is_active));
    setError(null);
    setSuccess(null);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        General Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send and edit system-wide notifications by role.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              SelectProps={{
                multiple: true,
                value: targetRoles,
                onChange: (e) => setTargetRoles(e.target.value as string[]),
                renderValue: (selected) => (selected as string[]).join(", "),
              }}
              label="Target Roles"
            >
              {ROLE_OPTIONS.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {editingNotificationId && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
            )}
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? editingNotificationId
                  ? "Updating..."
                  : "Sending..."
                : editingNotificationId
                  ? "Update Notification"
                  : "Send Notification"}
            </Button>
            {editingNotificationId && (
              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={submitting}
              >
                Cancel Edit
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Recent Notifications
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Target Roles</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && notifications.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No notifications found.</TableCell>
              </TableRow>
            )}
            {notifications.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.title}</TableCell>
                <TableCell>{item.message}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {(item.target_roles || []).map((role) => (
                      <Chip
                        key={`${item.id}-${role}`}
                        size="small"
                        label={role}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>{item.created_by_name || "System"}</TableCell>
                <TableCell>
                  {new Date(item.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEditNotification(item)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RoleNotificationManagement;
